import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Modal,
  Card,
  IconButton,
  Alert,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { Add as AddIcon } from "@mui/icons-material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import { getDSOKPromotions } from "app/services/api/promotion";
import JumboSearch from "@jumbo/components/JumboSearch";

const PromotionItemSortable = ({ id, promotion, onRemove, name }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        p: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
        {...attributes}
        {...listeners}
      >
        <DragIndicator sx={{ color: "text.secondary", fontSize: 20 }} />
        <Typography>{name}</Typography>
      </Box>
      <IconButton
        color="default"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(id);
        }}
      >
        <DeleteForeverIcon sx={{ color: "#000" }} />
      </IconButton>
    </Box>
  );
};

const CategoryPromotions = ({ category, onEditCategory, defaultLanguage }) => {
  const { t, i18n } = useTranslation();
  const [promotionModal, setPromotionModal] = useState();
  const [promotions, setPromotions] = useState([]);
  const [searchName, setSearchName] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getDSOKPromotions();
        const asArray = Array.isArray(data)
          ? data
          : data && typeof data === "object"
          ? Object.values(data)
          : [];
        setPromotions(asArray);
      } catch (e) {
        setPromotions([]);
      }
    };
    load();
  }, []);

  const selectedMap = React.useMemo(() => {
    const src = category?.promotions;
    const toObjectById = (arr) => {
      const obj = {};
      (arr || []).forEach((entry) => {
        const id = typeof entry === "string" ? entry : entry?._id || entry?.id;
        if (!id) return;
        const full =
          typeof entry === "object" && entry
            ? entry
            : promotions.find((p) => p?._id === id) || { _id: id };
        obj[id] = full;
      });
      return obj;
    };
    if (!src) return {};
    if (Array.isArray(src)) return toObjectById(src);
    if (typeof src === "object") {
      // handle corrupted char-object → try to reassemble JSON
      const keys = Object.keys(src);
      const looksLikeCharObj =
        keys.length > 0 && keys.every((k) => /^\d+$/.test(k));
      if (looksLikeCharObj) {
        try {
          const jsonString = Object.values(src).join("");
          const parsed = JSON.parse(jsonString);
          if (Array.isArray(parsed)) return toObjectById(parsed);
          if (parsed && typeof parsed === "object") return parsed;
        } catch {}
        return {};
      }
      return src;
    }
    if (typeof src === "string") {
      try {
        const parsed = JSON.parse(src);
        if (Array.isArray(parsed)) return toObjectById(parsed);
        if (parsed && typeof parsed === "object") return parsed;
      } catch {}
      return {};
    }
    return {};
  }, [category, promotions]);

  const selectedIds = React.useMemo(
    () => Object.keys(selectedMap),
    [selectedMap]
  );

  // Keep local optimistic order to avoid reverting before save completes
  const [localOrder, setLocalOrder] = useState([]);
  useEffect(() => {
    // Initialize local order once from server state; do not override optimistic changes
    if (!localOrder || localOrder.length === 0) {
      setLocalOrder(selectedIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(",")]);

  const orderedIds =
    localOrder && localOrder.length > 0 ? localOrder : selectedIds;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id && orderedIds.length > 0) {
      const oldIndex = orderedIds.indexOf(active.id);
      const newIndex = orderedIds.indexOf(over.id);
      const newOrder = arrayMove(orderedIds, oldIndex, newIndex);
      setLocalOrder(newOrder);
      const reordered = {};
      newOrder.forEach((id) => {
        reordered[id] = selectedMap[id];
      });
      onEditCategory({ ...category, promotions: reordered });
    }
  };

  const handleAddPromotion = () => {
    setSearchName("");
    setPromotionModal({ type: "addPromotion" });
  };

  const handleRemovePromotion = (promotionId) => {
    const next = { ...selectedMap };
    delete next[promotionId];
    onEditCategory({ ...category, promotions: next });
    // also update local order optimistically
    setLocalOrder((prev) => prev.filter((id) => id !== promotionId));
  };

  const filteredPromotions = React.useMemo(() => {
    const q = (searchName || "").toLowerCase();
    if (!q) return promotions;
    return promotions.filter((p) => {
      const nameObj = p?.name || {};
      const localized =
        nameObj?.[defaultLanguage || i18n.language] ||
        Object.values(nameObj).find((v) => typeof v === "string" && v) ||
        "";
      return String(localized).toLowerCase().includes(q);
    });
  }, [promotions, searchName, defaultLanguage, i18n.language]);

  const getPromotionName = (promotion) => {
    const nameObj = promotion?.name || {};
    const fallbackId = promotion?._id || promotion?.id || "";
    return (
      nameObj?.[defaultLanguage || i18n.language] ||
      Object.values(nameObj).find((v) => typeof v === "string" && v) ||
      fallbackId ||
      t("pages.dsokPromotions.title", { defaultValue: "Promotions" })
    );
  };

  const addToCategory = (promotion) => {
    const pid = promotion?._id;
    if (!pid) return;
    if (selectedMap[pid]) {
      setPromotionModal();
      return;
    }
    const next = { ...selectedMap, [pid]: promotion };
    onEditCategory({ ...category, promotions: next });
    // Optimistically update local order so the new item appears immediately
    setLocalOrder((prev) =>
      prev && prev.length > 0 ? [...prev, pid] : Object.keys(next)
    );
    setPromotionModal();
  };

  const hasPromotions = selectedIds.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        <Alert variant="outlined" severity="info">
          {t("pages.dsokCategories.addPromotionInfo")}
        </Alert>
      </Typography>
      <Div
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPromotion}
          size="small"
        >
          {t("buttons.addPromotion", { defaultValue: "Add promotion" })}
        </Button>
      </Div>

      {hasPromotions ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedIds}
            strategy={verticalListSortingStrategy}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              {orderedIds.map((id) => {
                const promotion = selectedMap[id];
                return (
                  <PromotionItemSortable
                    key={id}
                    id={id}
                    promotion={promotion}
                    onRemove={handleRemovePromotion}
                    name={getPromotionName(promotion)}
                  />
                );
              })}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 1,
            p: 4,
            mt: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            <JumboListNoDataPlaceholder />
          </Typography>
        </Box>
      )}

      <Modal
        open={promotionModal !== undefined}
        onClose={() => setPromotionModal()}
      >
        <Div
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "75vw",
            maxHeight: "70vh",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          {promotionModal?.type === "addPromotion" && (
            <React.Fragment>
              <Div sx={{ mb: 2 }}>
                <Typography variant={"h2"} mb={3}>
                  {t("pages.dsokPromotions.title", {
                    defaultValue: "Promotions",
                  })}
                </Typography>
              </Div>
              <Box sx={{ mb: 2 }}>
                <Card sx={{ mb: 0, width: "50%" }}>
                  <JumboSearch
                    page="DSOKPromotions"
                    onChange={setSearchName}
                    sx={{ width: "100%" }}
                    placeholder={t("pages.presentation.searchPresentation")}
                  />
                </Card>
              </Box>
              <Div sx={{ mb: 2 }}>
                {(filteredPromotions || []).map((p, index) => (
                  <Card
                    key={p?._id || index}
                    sx={{
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ flex: 1 }}>
                      {getPromotionName(p)}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => addToCategory(p)}
                    >
                      {t("buttons.add")}
                    </Button>
                  </Card>
                ))}
              </Div>
            </React.Fragment>
          )}
        </Div>
      </Modal>
    </Box>
  );
};

export default CategoryPromotions;
